import type {
  ApiExerciseStatistics,
  ApiStrengthStatistics,
  ApiTrainingOverview,
  ApiCardioStatistics,
  ApiHrZoneBreakdown,
  ApiHrZoneTotals,
} from '../types/stats.types';
import type {
  UiExerciseStatistics,
  UiStrengthStatistics,
  UiTrainingOverview,
  UiCardioStatistics,
  UiHrZoneTotals,
  UiHrZones,
} from '../../schemas/trainingStats.schema';

const ZONE_KEYS = ['z1', 'z2', 'z3', 'z4', 'z5'] as const;

function mapHrZoneTotals(
  totals: ApiHrZoneTotals,
  ranges: ApiHrZoneBreakdown['ranges'],
): UiHrZoneTotals {
  return {
    sessionsTotal: totals.sessionsTotal,
    sessionsWithZones: totals.sessionsWithZones,
    totalSec: totals.totalSec,
    zones: ZONE_KEYS.map((key, i) => ({
      max: ranges?.[key].max ?? null,
      min: ranges?.[key].min ?? null,
      seconds: totals.secondsByZone[i] ?? 0,
      zone: i + 1,
    })),
  };
}

export function mapHrZonesFromApi(breakdown: ApiHrZoneBreakdown): UiHrZones {
  const { byScope, ranges } = breakdown;
  return {
    rangesChanged: breakdown.rangesChanged,
    scopes: {
      all: mapHrZoneTotals(byScope.all, ranges),
      cardio: mapHrZoneTotals(byScope.cardio, ranges),
      strength: mapHrZoneTotals(byScope.strength, ranges),
    },
  };
}

export function mapCardioStatisticsFromApi(
  api: ApiCardioStatistics,
): UiCardioStatistics {
  return {
    distance: api.distance,
    duration: api.duration,
    equipment: api.byEquipment.map(({ equipmentTagId, ...rest }) => ({
      ...rest,
      id: equipmentTagId,
    })),
    excludedSetsWithoutTime: api.excludedSetsWithoutTime,
    hr: api.hr,
    hrZones: mapHrZonesFromApi(api.hrZones),
    kcal: api.kcal,
    sessions: api.sessions,
    steps: api.steps,
  };
}

export function mapStrengthStatisticsFromApi(
  api: ApiStrengthStatistics,
): UiStrengthStatistics {
  return {
    avgRpe: api.avgRpe,
    holdKgSec: api.holdKgSec,
    hrZones: mapHrZonesFromApi(api.hrZones),
    kcal: api.kcal,
    loadKgRep: api.loadKgRep,
    loadPerRepKg: api.loadPerRepKg,
    muscleGroups: api.muscleGroups,
    newRecords: api.newRecords,
    restPerSetSec: api.restPerSetSec,
    sessions: api.sessions,
    totalReps: api.totalReps,
    totalSets: api.totalSets,
    workouts: api.workouts.map((workout) => ({
      date: workout.businessDate,
      id: workout.sessionId,
      kcal: workout.kcal,
      kcalPerMin: workout.kcalPerMin,
      loadKgRep: workout.loadKgRep,
      name: workout.name,
      restPerSetSec: workout.restPerSetSec,
      totalReps: workout.totalReps,
      totalSets: workout.totalSets,
    })),
  };
}

export function mapTrainingOverviewFromApi(
  api: ApiTrainingOverview,
): UiTrainingOverview {
  return {
    activities: api.activities,
    classes: api.classes,
    duration: api.duration,
    effort: api.effort,
    floors: api.floors,
    hr: api.hr,
    hrZones: mapHrZonesFromApi(api.hrZones),
    kcal: api.kcal,
    load: api.load,
    recovery: api.recovery,
    sessions: api.sessions,
    steps: api.steps,
    time: api.time,
    weeks: api.weeks.map((week) => ({
      durationSec: week.durationSec,
      from: week.from,
      partial: week.partial,
      recovery: week.recovery,
      to: week.to,
      weekStart: week.weekStart,
      workouts: week.workouts,
    })),
    workouts: api.workouts,
  };
}

export function mapExerciseStatisticsFromApi(
  api: ApiExerciseStatistics,
): UiExerciseStatistics {
  return {
    current: api.current
      ? {
          measuredAt: api.current.measuredAt,
          previous: api.current.previous,
          value: api.current.value,
        }
      : null,
    entries: api.entries.map((entry) => ({
      id: entry.id,
      measuredAt: entry.measuredAt,
      note: entry.note,
      value: entry.value,
    })),
    firstSessionDate: api.firstSessionDate,
    id: api.exercise.id,
    metric: api.metric,
    months: api.months.map((month) => ({
      month: month.month,
      reference: month.reference,
      workout: month.workout,
    })),
    name: api.exercise.name,
    sessions: api.sessions.map((session) => ({
      date: session.date,
      percent: session.percent,
      reps: session.reps,
      value: session.value,
    })),
  };
}
